import { legalVersionSchema } from "@/features/legal/version";
import { z } from "zod/v4";

import { socialLinksSchema, normalizeSocialUrl } from "./social-links";
import type { SocialMetadata } from "@/features/client-metadata/schema";

// Mirrors users/profile-social-links schemas in tripbears-api.
export const ageBrackets = [
  "UNDER_18",
  "AGE_18_24",
  "AGE_25_34",
  "AGE_35_44",
  "AGE_45_54",
  "AGE_55_64",
  "AGE_65_PLUS",
] as const;
export const ageLabels: Record<(typeof ageBrackets)[number], string> = {
  UNDER_18: "Under 18",
  AGE_18_24: "18–24",
  AGE_25_34: "25–34",
  AGE_35_44: "35–44",
  AGE_45_54: "45–54",
  AGE_55_64: "55–64",
  AGE_65_PLUS: "65+",
};

export const usernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(
    /^[a-z0-9][a-z0-9._-]{1,28}[a-z0-9]$/,
    "Use 3–30 letters, numbers, dots, underscores or hyphens; start and end with a letter or number.",
  );

export const profileSchema = z
  .object({
    username: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    imageUrl: z.string().nullable(),
    homeCountryId: z.string(),
    language: z.string().nullable(),
    location: z.string().nullable(),
    currency: z.string(),
    ageBracket: z.enum(ageBrackets).nullable(),
    profileVisible: z.boolean(),
    searchable: z.boolean(),
    socialLinks: socialLinksSchema,
    // An unreadable or missing acceptance never proves consent. Keep the rest of
    // the profile usable so the legal gate can request a fresh numeric acceptance.
    legalLastAcceptedAt: z.string().nullable(),
    legalAcceptedVersion: legalVersionSchema.nullable().catch(null),
    updatedAt: z.string(),
  })
  .passthrough();
export type Profile = z.infer<typeof profileSchema>;

const formSchema = z.object({
  username: z.string(),
  firstName: z.string().trim(),
  lastName: z.string().trim(),
  location: z.string().trim(),
  language: z.string().trim(),
  currency: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{3}$/, "Choose a three-letter currency code."),
  ageBracket: z.enum(ageBrackets).nullable(),
  profileVisible: z.boolean(),
  searchable: z.boolean(),
  socialLinks: socialLinksSchema,
});
export type ProfileFormValues = z.infer<typeof formSchema>;
export function profileFormSchema(profile: Profile, metadata?: SocialMetadata) {
  return formSchema.superRefine((values, ctx) => {
    if (
      values.username.trim().toLowerCase() !== (profile.username ?? "") &&
      !usernameSchema.safeParse(values.username).success
    )
      ctx.addIssue({
        code: "custom",
        path: ["username"],
        message:
          "Use 3–30 letters, numbers, dots, underscores or hyphens; start and end with a letter or number.",
      });
    if (metadata)
      values.socialLinks.forEach((link, index) => {
        if (
          link.id !== "custom" &&
          !metadata.options.some((option) => option.id === link.id)
        )
          ctx.addIssue({
            code: "custom",
            path: ["socialLinks", index, "id"],
            message:
              "This platform is unavailable. Remove the link or retry metadata.",
          });
        if (!normalizeSocialUrl(link.url, metadata.urlRules))
          ctx.addIssue({
            code: "custom",
            path: ["socialLinks", index, "url"],
            message: "This URL does not match the current server rules.",
          });
      });
    for (const field of ["firstName", "lastName"] as const) {
      if (profile[field]?.trim() && !values[field])
        ctx.addIssue({
          code: "custom",
          path: [field],
          message: `Enter your ${field === "firstName" ? "first" : "last"} name. Existing names cannot be cleared.`,
        });
    }
  });
}
export function profileFormValues(profile: Profile): ProfileFormValues {
  return {
    username: profile.username ?? "",
    firstName: profile.firstName ?? "",
    lastName: profile.lastName ?? "",
    location: profile.location ?? "",
    language: profile.language ?? "",
    currency: profile.currency,
    ageBracket: profile.ageBracket,
    profileVisible: profile.profileVisible,
    searchable: profile.searchable,
    socialLinks: profile.socialLinks.map((link) => ({ ...link })),
  };
}
const updateSchema = z.object({
  username: usernameSchema.optional(),
  firstName: z.string().trim().min(1).optional(),
  lastName: z.string().trim().min(1).optional(),
  language: z.string().min(1).nullable().optional(),
  location: z.string().min(1).nullable().optional(),
  currency: z.string().length(3).optional(),
  ageBracket: z.enum(ageBrackets).nullable().optional(),
  profileVisible: z.boolean().optional(),
  searchable: z.boolean().optional(),
  socialLinks: socialLinksSchema.optional(),
});
export type ProfilePatch = z.infer<typeof updateSchema>;
function normalizedValues(
  values: ProfileFormValues,
  metadata?: SocialMetadata,
) {
  return {
    username: values.username.trim().toLowerCase(),
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    location: values.location.trim() || null,
    language: values.language.trim() || null,
    currency: values.currency.trim().toUpperCase(),
    ageBracket: values.ageBracket,
    profileVisible: values.profileVisible,
    searchable: values.searchable,
    socialLinks: values.socialLinks.map((link) =>
      link.id === "custom"
        ? {
            id: link.id,
            platform: link.platform.trim(),
            url: normalizeSocialUrl(link.url, metadata?.urlRules)!,
          }
        : {
            id: link.id,
            url: normalizeSocialUrl(link.url, metadata?.urlRules)!,
          },
    ),
  };
}
export function buildProfilePatch(
  values: ProfileFormValues,
  baseline: Profile,
  metadata?: SocialMetadata,
): ProfilePatch {
  const next = normalizedValues(
    profileFormSchema(baseline, metadata).parse(values),
    metadata,
  );
  const previous = normalizedValues(profileFormValues(baseline), metadata);
  const patch: Record<string, unknown> = {};
  for (const field of [
    "username",
    "firstName",
    "lastName",
    "location",
    "language",
    "currency",
    "ageBracket",
    "searchable",
    "profileVisible",
  ] as const) {
    if (next[field] !== previous[field]) patch[field] = next[field];
  }
  if (JSON.stringify(next.socialLinks) !== JSON.stringify(previous.socialLinks))
    patch.socialLinks = next.socialLinks;
  return updateSchema.parse(patch);
}
